/**
 * Panorama IA — /admin/ai (PR-4 builder reorg, admin-only).
 *
 * The cross-project AI console per the frozen redesign:
 *
 *   - "Resumen" (default): 4 KPI tiles from the issue metrics + a
 *     weekly line chart (`LineChartT` from bi-components — no chart
 *     libs) + the "dónde se trabaja cada cosa" orientation card.
 *   - "Cola de issues": the shared `IssuesQueue` in cross-project
 *     mode, with the open count as a tab badge.
 *   - "Prompts globales": the EXISTING `PromptsPage` embedded as-is
 *     (the /admin/prompts URL now redirects here; the /:id editor
 *     page keeps its own route).
 *
 * Tab state mirrored to `?tab=` (deep-linkable).
 */

import { Card, CardBody, Tabs, type TabItem } from "@omnitok/ui";
import { AlertCircle } from "lucide-react";
import { LineChartT, PALETTE, Spinner } from "@pervasivemind/bi-components";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useT } from "@/i18n";
import { PageContainer, PageHeader } from "@pervasivemind/admin-ui";
import { adminIssuesApi, type IssueMetrics } from "@/lib/admin-ai-api";
import { PromptsPage } from "@/pages/admin/PromptsPage";
import { IssuesQueue } from "@/pages/panorama/IssuesQueue";

type PanoramaTab = "overview" | "issues" | "prompts";

const VALID_TABS: ReadonlyArray<PanoramaTab> = [
  "overview",
  "issues",
  "prompts",
];

/** The series colour of the weekly issues chart: S2 of the one series
 *  palette (brand.js, DQ3), the kit's action pink. */
const CHART_ACCENT = PALETTE[1];

export function PanoramaIAPage(): JSX.Element {
  const { t } = useT();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("tab") as PanoramaTab | null;
  const activeTab: PanoramaTab =
    rawTab && VALID_TABS.includes(rawTab) ? rawTab : "overview";
  const setActiveTab = (tab: PanoramaTab): void => {
    setSearchParams(tab === "overview" ? {} : { tab }, { replace: true });
  };

  const [metrics, setMetrics] = useState<IssueMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMetrics = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setMetrics(await adminIssuesApi.metrics());
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  const tabs: TabItem[] = [
    { id: "overview", label: t("panorama.tab.overview") },
    {
      id: "issues",
      label: t("panorama.tab.issues"),
      badge:
        metrics && metrics.open_count > 0 ? metrics.open_count : undefined,
    },
    { id: "prompts", label: t("panorama.tab.prompts") },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t("panorama.title")}
        subtitle={t("panorama.subtitle")}
      />

      <div className="mb-6 border-b border-neutral-200">
        <Tabs
          items={tabs}
          activeId={activeTab}
          onChange={(id: string) => setActiveTab(id as PanoramaTab)}
          variant="underline"
          size="sm"
        />
      </div>

      {activeTab === "overview" && (
        <OverviewPane
          metrics={metrics}
          loading={loading}
          error={error}
          onRetry={() => void loadMetrics()}
        />
      )}
      {activeTab === "issues" && (
        <IssuesQueue
          onChanged={() => void loadMetrics()}
          openCount={metrics?.open_count ?? null}
        />
      )}
      {activeTab === "prompts" && <PromptsPage />}
    </PageContainer>
  );
}

// ───────────────────────────────────────────────────────────
// Resumen
// ───────────────────────────────────────────────────────────

interface OverviewPaneProps {
  metrics: IssueMetrics | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

function OverviewPane({
  metrics,
  loading,
  error,
  onRetry,
}: OverviewPaneProps): JSX.Element {
  const { t } = useT();

  if (loading && !metrics) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-neutral-500">
        <Spinner size={16} ariaLabel={t("common.loading")} />
        {t("common.loading")}
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <Card variant="bordered" className="border-error/40 bg-error/5">
        <CardBody>
          <div className="flex items-start gap-3 text-sm text-error-dark">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">{error ?? t("common.error")}</p>
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 text-xs font-medium underline"
              >
                {t("common.retry")}
              </button>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  const weeklyData = metrics.weekly_series.map((w) => ({
    label: w.week_start,
    value: w.count,
  }));

  return (
    <div className="space-y-4">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile
          label={t("panorama.kpi.open")}
          value={metrics.open_count.toLocaleString()}
        />
        <KpiTile
          label={t("panorama.kpi.week")}
          value={metrics.issues_this_week.toLocaleString()}
        />
        <KpiTile
          label={t("panorama.kpi.topReport")}
          value={metrics.top_report ?? "—"}
          mono
        />
        <KpiTile
          label={t("panorama.kpi.topHeuristic")}
          value={metrics.top_heuristic ?? "—"}
          mono
        />
      </div>

      {/* Weekly chart */}
      {weeklyData.length > 0 && (
        <Card variant="bordered" noPadding>
          <div className="border-b border-neutral-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-neutral-900">
              {t("panorama.chart.title")}
            </h3>
            <p className="text-xs text-neutral-500">
              {t("panorama.chart.subtitle")}
            </p>
          </div>
          <div className="p-4">
            <LineChartT
              data={weeklyData}
              color={CHART_ACCENT}
              height={220}
              ariaLabel={t("panorama.chart.title")}
            />
          </div>
        </Card>
      )}

      {/* Orientation card: where each thing is managed */}
      <Card variant="bordered">
        <CardBody>
          <h3 className="text-sm font-semibold text-neutral-900">
            {t("panorama.map.title")}
          </h3>
          <dl className="mt-2 space-y-2 text-xs">
            <div>
              <dt className="font-semibold uppercase tracking-wide text-accent-dark">
                {t("panorama.map.report")}
              </dt>
              <dd className="mt-0.5 text-neutral-600">
                {t("panorama.map.reportItems")}
              </dd>
            </div>
            <div>
              <dt className="font-semibold uppercase tracking-wide text-primary">
                {t("panorama.map.project")}
              </dt>
              <dd className="mt-0.5 text-neutral-600">
                {t("panorama.map.projectItems")}
              </dd>
            </div>
            <div>
              <dt className="font-semibold uppercase tracking-wide text-neutral-500">
                {t("panorama.map.global")}
              </dt>
              <dd className="mt-0.5 text-neutral-600">
                {t("panorama.map.globalItems")}
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>
    </div>
  );
}

function KpiTile({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-1 text-xs text-neutral-500">
        {label}
      </div>
      <div
        className={`truncate font-semibold text-neutral-900 ${
          mono ? "font-mono text-base" : "text-2xl"
        }`}
        title={value}
      >
        {value}
      </div>
    </div>
  );
}
